import UnfoldLess from '@mui/icons-material/UnfoldLess';
import UnfoldMore from '@mui/icons-material/UnfoldMore';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { t } from 'i18next';
import { useState } from 'react';

import { type JobEventSimulation } from './JobEventSimulation';
import {
  COLUMN_COUNT,
  EmptyRowCells,
  EventCells,
  HeaderRow,
  stripedRowSx,
  tableSx,
  titleCellSx,
} from './jobEventsTableShared';

interface DiffRow {
  historical: JobEventSimulation | null;
  current: JobEventSimulation | null;
  sortTime: number;
}

interface JobEventsDiffTableProps {
  diffRows: DiffRow[];
  simulatedEvents: JobEventSimulation[];
  historicalEvents: JobEventSimulation[];
}

export default function JobEventsDiffTable({
  diffRows,
  simulatedEvents,
  historicalEvents,
}: JobEventsDiffTableProps) {
  const [expanded, setExpanded] = useState(true);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        gap: 1,
        minWidth: 0,
      }}
    >
      {/* simulated (current) */}
      <TableContainer component={Paper} sx={{ flex: 1, minWidth: 0, ...tableSx }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                colSpan={COLUMN_COUNT}
                sx={{
                  ...titleCellSx,
                  position: 'relative',
                }}
              >
                {t('admin.job_geo_debug.diff_current')}
                <IconButton
                  size="small"
                  onClick={() => setExpanded((prev) => !prev)}
                  title={t('admin.job_geo_debug.diff_view_toggle')}
                  aria-label={t('admin.job_geo_debug.diff_view_toggle')}
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    right: 4,
                    transform: 'translateY(-50%)',
                    p: 0.25,
                    '& .MuiSvgIcon-root': { fontSize: 16 },
                  }}
                >
                  {expanded ? (
                    <UnfoldLess fontSize="inherit" />
                  ) : (
                    <UnfoldMore fontSize="inherit" />
                  )}
                </IconButton>
              </TableCell>
            </TableRow>
            <HeaderRow />
          </TableHead>
          <TableBody>
            {expanded ? (
              diffRows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={COLUMN_COUNT}
                    align="center"
                    sx={{ py: 0.5, color: 'text.secondary' }}
                  >
                    {t('admin.job_geo_debug.no_job_events')}
                  </TableCell>
                </TableRow>
              ) : (
                diffRows.map((diffRow, index) => {
                  const event = diffRow.current;
                  const isAdded = diffRow.historical === null;
                  return (
                    <TableRow
                      key={`${diffRow.sortTime}-${event?.id ?? 'empty'}-${index}-right`}
                      sx={{
                        '&:hover': {
                          backgroundColor: (theme) =>
                            isAdded
                              ? 'rgba(76, 175, 80, 0.24)'
                              : theme.palette.action.hover,
                        },
                        backgroundColor: (theme) =>
                          isAdded
                            ? 'rgba(76, 175, 80, 0.12)'
                            : index % 2 === 1
                              ? theme.semantic.surfaceSunken
                              : 'transparent',
                      }}
                    >
                      {event ? <EventCells event={event} /> : <EmptyRowCells />}
                    </TableRow>
                  );
                })
              )
            ) : simulatedEvents.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={COLUMN_COUNT}
                  align="center"
                  sx={{ py: 0.5, color: 'text.secondary' }}
                >
                  {t('admin.job_geo_debug.no_job_events')}
                </TableCell>
              </TableRow>
            ) : (
              simulatedEvents.map((event, index) => (
                <TableRow
                  key={`${event.id ?? index}-${event.eventName}`}
                  sx={{
                    '&:hover': {
                      backgroundColor: (theme) => theme.palette.action.hover,
                    },
                    ...stripedRowSx(index),
                  }}
                >
                  <EventCells event={event} />
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* historical */}
      <TableContainer component={Paper} sx={{ flex: 1, minWidth: 0, ...tableSx }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell colSpan={COLUMN_COUNT} sx={titleCellSx}>
                {t('admin.job_geo_debug.diff_historical')}
              </TableCell>
            </TableRow>
            <HeaderRow />
          </TableHead>
          <TableBody>
            {expanded ? (
              diffRows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={COLUMN_COUNT}
                    align="center"
                    sx={{ py: 0.5, color: 'text.secondary' }}
                  >
                    {t('admin.job_geo_debug.no_job_events')}
                  </TableCell>
                </TableRow>
              ) : (
                diffRows.map((diffRow, index) => {
                  const event = diffRow.historical;
                  const isRemoved = diffRow.current === null;
                  return (
                    <TableRow
                      key={`${diffRow.sortTime}-${event?.id ?? 'empty'}-${index}-left`}
                      sx={{
                        '&:hover': {
                          backgroundColor: (theme) =>
                            isRemoved
                              ? 'rgba(244, 67, 54, 0.18)'
                              : theme.palette.action.hover,
                        },
                        backgroundColor: (theme) =>
                          isRemoved
                            ? 'rgba(244, 67, 54, 0.08)'
                            : index % 2 === 1
                              ? theme.semantic.surfaceSunken
                              : 'transparent',
                      }}
                    >
                      {event ? <EventCells event={event} /> : <EmptyRowCells />}
                    </TableRow>
                  );
                })
              )
            ) : historicalEvents.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={COLUMN_COUNT}
                  align="center"
                  sx={{ py: 0.5, color: 'text.secondary' }}
                >
                  {t('admin.job_geo_debug.no_job_events')}
                </TableCell>
              </TableRow>
            ) : (
              historicalEvents.map((event, index) => (
                <TableRow
                  key={`${event.id ?? index}-${event.eventName}`}
                  sx={{
                    '&:hover': {
                      backgroundColor: (theme) => theme.palette.action.hover,
                    },
                    ...stripedRowSx(index),
                  }}
                >
                  <EventCells event={event} />
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
